import { Divider, Drawer, Skeleton } from '@mui/material';
import {
  IconMinusVertical,
  IconMoon,
  IconPlaneArrival,
  IconPlaneDeparture,
  IconScubaMask,
  IconSun,
  IconUser,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useAuth0 } from '../../auth/AuthProvider';
import TripManagementDetail from './TripManagementDetail';
import useUpcomingBookingSummary from '../../hooks/useUpcomingBookingSummary';
import {
  formatDate,
  formatDateRange,
  formatTime,
} from '../../common/utilities/dateFormatter';
import { UpcomingBookingSummary } from '../../gql/graphql';
import { useNavigate } from 'react-router-dom';
import TripItineraryModal from './components/TripItineraryModal';
import { computeTripDuration } from '../../utils/tripDuration';
import { getTripManagementStatus } from '../../utils/tripManagementStatus';

const TripManagement = () => {
  const { user } = useAuth0();

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // 0 for Upcoming, 1 for Completed
  const [selectedTripId, setSelectedTripId] = useState<number | null>(null); // State for selected trip ID
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal

  // Fetch data using the custom hook, only if the user is authenticated
  const { upcomingBookings, isLoading, error } = useUpcomingBookingSummary(
    user?.sub || '',
  );

  const handleOpenTripDetail = (tripId: number) => {
    setSelectedTripId(tripId);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTripId(null);
  };

  // Open and close modal functions
  const handleOpenModal = (tripId: number) => {
    setSelectedTripId(tripId); // Set the selected trip ID when opening the modal
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTripId(null);
  };

  // Navigate to home page
  const navigateToHome = () => {
    navigate('/');
  };

  // Separate trips into upcoming and completed based on the current date
  const currentDate = new Date();
  const upcomingTrips = upcomingBookings.filter(
    booking => new Date(booking.disembarkationDate) >= currentDate,
  );
  const completedTrips = upcomingBookings.filter(
    booking => new Date(booking.disembarkationDate) < currentDate,
  );

  const renderTab = (
    tabName: string,
    numberOfTrip: number,
    tabIndex: number,
  ) => {
    const isActive = tabIndex === activeTab;

    return (
      <button
        type="button"
        role="tab"
        id={`trips-tab-${tabIndex}`}
        aria-selected={isActive}
        aria-controls={`trips-tabpanel-${tabIndex}`}
        tabIndex={isActive ? 0 : -1}
        className={`flex pb-2 justify-center items-start border-b-2 ${
          isActive ? 'border-neutral-800' : 'border-transparent'
        }`}
        onClick={() => setActiveTab(tabIndex)}
      >
        <span
          className={`${
            isActive ? 'text-neutral-800 font-semibold' : 'text-neutral-500'
          }`}
        >
          {tabName} ({numberOfTrip})
        </span>
      </button>
    );
  };

  const renderTripsLoading = () => (
    <div className="flex flex-col gap-6" aria-busy="true" aria-live="polite">
      {[0, 1].map(item => (
        <div key={item} className="flex flex-col md:flex-row gap-4">
          <Skeleton variant="rounded" width={96} height={96} />
          <div className="flex-1 flex flex-col gap-3">
            <Skeleton width="40%" height={28} />
            <Skeleton width="60%" height={20} />
            <Skeleton width="80%" height={20} />
          </div>
        </div>
      ))}
    </div>
  );

  const renderTripsError = () => (
    <div
      className="flex flex-col items-start gap-3 rounded-lg border border-status-error/30 bg-red-50 p-4"
      role="alert"
    >
      <span className="text-neutral-800 font-semibold">
        Could not load your trips
      </span>
      <span className="text-neutral-600 text-sm">
        Check your connection and refresh the page. If this keeps happening,
        email help@santtai.com.
      </span>
      <button
        type="button"
        className="underline font-semibold text-neutral-800"
        onClick={() => window.location.reload()}
      >
        Retry
      </button>
    </div>
  );

  const renderTrips = (
    trips: UpcomingBookingSummary[],
    isUpcoming: boolean,
  ) => {
    if (isLoading) return renderTripsLoading();
    if (error) return renderTripsError();
    if (!trips.length)
      return (
        <div className="flex flex-col justify-end items-center gap-6 self-stretch">
          <img
            src={
              isUpcoming
                ? 'https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/bae20ae8-ada0-44de-60f4-74eb3644ae00/public'
                : 'https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/817997e2-49dd-43a2-c593-aaa2f19b0e00/public'
            }
            alt={
              isUpcoming
                ? 'shine young woman freediver swim with a dolphin'
                : 'no completed trips available'
            }
          />
          <div className="flex flex-col items-center gap-2 self-stretch">
            <span className="text-2xl font-semibold text-neutral-800">
              No {isUpcoming ? 'upcoming' : 'completed'} trip
            </span>
            {isUpcoming && (
              <span className="text-neutral-500 text-center">
                Book your liveaboard trip to start your diving journey
              </span>
            )}
          </div>
          {isUpcoming && (
            <button
              type="button"
              className="flex px-6 py-3 justify-center items-center gap-2 self-stretch rounded-lg bg-brand-santtai-primary"
              onClick={navigateToHome}
            >
              <span className="font-semibold text-neutral-00">
                Search liveaboard trips
              </span>
            </button>
          )}
        </div>
      );

    return trips.map((booking, index) => {
      const tripStatus = getTripManagementStatus(booking.bookingStatus);
      const duration = computeTripDuration(
        booking.embarkationDate,
        booking.disembarkationDate,
      );

      return (
        <div key={`${booking.bookingId}-${booking.tripId}-${index}`}>
          {/* Mobile view */}
          <div
            className="flex flex-col items-start gap-6 md:hidden cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={() => handleOpenTripDetail(booking.tripId)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleOpenTripDetail(booking.tripId);
              }
            }}
          >
            <div className="w-full">
              <div className="flex flex-col items-start gap-6 self-stretch">
                <div className="flex items-start gap-4 self-stretch">
                  <img
                    className={`w-[3.75rem] h-[3.75rem] rounded-lg ${
                      isUpcoming ? '' : 'filter grayscale'
                    }`} // Apply grayscale for completed trips
                    src={booking.boatPhoto}
                    alt="boat image"
                  />
                  <div className="flex flex-col items-start content-center gap-2 flex-wrap">
                    <span className="text-xl font-semibold text-neutral-800">
                      {booking.boatName}
                    </span>
                    <div
                      className={`flex py-1 px-2 items-start gap-2 rounded ${tripStatus.className}`}
                    >
                      <span className="text-sm font-semibold">
                        {tripStatus.label}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-start justify-between self-stretch mt-6">
                <div className="flex flex-col items-start gap-2">
                  <span className="font-semibold text-neutral-800">
                    {formatDateRange(
                      booking.embarkationDate,
                      booking.disembarkationDate,
                      true,
                    )}
                  </span>
                  <div className="flex flex-start text-sm text-neutral-500">
                    <div className="flex items-center gap-1">
                      <IconSun size={20} />
                      <span>{duration ? `${duration.days}D` : 'N/A'}</span>
                    </div>
                    <div className="flex items-center flex-start gap-2 text-neutral-100">
                      <IconMinusVertical size={20} />
                    </div>
                    <div className="flex items-center gap-1">
                      <IconMoon size={20} />
                      <span>{duration ? `${duration.nights}N` : 'N/A'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-start gap-2">
                  <span className="font-semibold text-neutral-800">
                    {booking.routeName}
                  </span>
                  <div className="flex flex-start text-sm text-neutral-500">
                    <div className="flex items-center gap-1">
                      <IconScubaMask size={20} />
                      <span>{booking.diveCount} dives</span>
                    </div>
                    <div className="flex items-center flex-start gap-2 text-neutral-100">
                      <IconMinusVertical size={20} />
                    </div>
                    <div className="flex items-center gap-1">
                      <IconUser size={20} />
                      <span>{booking.guestCount} guests</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-start justify-between self-stretch mt-4">
                <div className="flex flex-col items-start gap-2">
                  <div className="flex items-start gap-2">
                    <IconPlaneArrival size={20} />
                    <span className="font-semibold text-neutral-800">
                      Arrival
                    </span>
                  </div>
                  <div className="flex flex-col content-center items-start gap-2 text-neutral-500 text-sm">
                    <span>{booking.arrivalAirport}</span>
                    <span>{formatDate(booking.embarkationDate)}</span>
                    <span>{formatTime(booking.arrivalTime)}</span>
                  </div>
                </div>
                <div className="flex flex-col items-start gap-2">
                  <div className="flex items-start gap-2">
                    <IconPlaneDeparture size={20} />
                    <span className="font-semibold text-neutral-800">
                      Departure
                    </span>
                  </div>
                  <div className="flex flex-col content-center items-start gap-2 text-neutral-500 text-sm">
                    <span>{booking.departureAirport}</span>
                    <span>{formatDate(booking.disembarkationDate)}</span>
                    <span>{formatTime(booking.departureTime)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full">
              <Divider />
            </div>
          </div>

          {/* Desktop view */}
          <div className="hidden md:flex items-start gap-8">
            <img
              className={`w-24 h-24 rounded-lg ${
                isUpcoming ? '' : 'filter grayscale'
              }`} // Apply grayscale for completed trips
              src={booking.boatPhoto}
              alt="boat image"
            />
            <div className="flex flex-col items-start gap-6">
              <div className="flex flex-col items-start gap-4 self-stretch">
                <div className="flex items-center gap-4">
                  <span className="text-xl font-semibold text-neutral-800">
                    {booking.boatName}
                  </span>
                  <div
                    className={`flex py-1 px-2 items-start gap-2 rounded ${tripStatus.className}`}
                  >
                    <span className="text-sm font-semibold">
                      {tripStatus.label}
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-12 self-stretch">
                  <div className="flex flex-col items-start gap-2">
                    <span className="font-semibold text-neutral-800">
                      {formatDateRange(
                        booking.embarkationDate,
                        booking.disembarkationDate,
                        true,
                      )}
                    </span>
                    <div className="flex flex-start gap-2 text-sm text-neutral-500">
                      <div className="flex items-center gap-1">
                        <IconSun size={20} />
                        <span>{duration ? `${duration.days}D` : 'N/A'}</span>
                      </div>
                      <div className="flex items-center flex-start gap-2 text-neutral-100">
                        <IconMinusVertical size={20} />
                      </div>
                      <div className="flex items-center gap-1">
                        <IconMoon size={20} />
                        <span>{duration ? `${duration.nights}N` : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <span className="font-semibold text-neutral-800">
                      {booking.routeName}
                    </span>
                    <div className="flex flex-start gap-2 text-sm text-neutral-500">
                      <div className="flex items-center gap-1">
                        <IconScubaMask size={20} />
                        <span>{booking.diveCount} dives</span>
                      </div>
                      <div className="flex items-center flex-start gap-2 text-neutral-100">
                        <IconMinusVertical size={20} />
                      </div>
                      <div className="flex items-center gap-1">
                        <IconUser size={20} />
                        <span>{booking.guestCount} guests</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <div className="flex items-center gap-2">
                      <div className="flex w-[7.5rem] items-start gap-2">
                        <IconPlaneArrival size={20} />
                        <span className="font-semibold text-neutral-800">
                          Arrival
                        </span>
                      </div>
                      <div className="flex items-start gap-2 text-neutral-500 text-sm">
                        <span>{booking.arrivalAirport}</span>
                        <div className="flex items-center flex-start gap-2 text-neutral-100">
                          <IconMinusVertical size={20} />
                        </div>
                        <span>{formatDate(booking.embarkationDate)}</span>
                        <div className="flex items-center flex-start gap-2 text-neutral-100">
                          <IconMinusVertical size={20} />
                        </div>
                        <span>{formatTime(booking.arrivalTime)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex w-[7.5rem] items-start gap-2">
                        <IconPlaneDeparture size={20} />
                        <span className="font-semibold text-neutral-800">
                          Departure
                        </span>
                      </div>
                      <div className="flex items-start gap-2 text-neutral-500 text-sm">
                        <span>{booking.departureAirport}</span>
                        <div className="flex items-center flex-start gap-2 text-neutral-100">
                          <IconMinusVertical size={20} />
                        </div>
                        <span>{formatDate(booking.disembarkationDate)}</span>
                        <div className="flex items-center flex-start gap-2 text-neutral-100">
                          <IconMinusVertical size={20} />
                        </div>
                        <span>{formatTime(booking.departureTime)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  className="flex py-3 px-6 gap-2 border border-neutral-100 rounded-lg"
                  onClick={() => handleOpenTripDetail(booking.tripId)}
                >
                  Manage my trip
                </button>
                <button
                  type="button"
                  className="font-semibold underline text-neutral-800"
                  onClick={() => handleOpenModal(booking.tripId)} // Pass trip ID when opening modal
                >
                  Check trip itinerary
                </button>
              </div>
            </div>
          </div>
          <div className="hidden md:block my-6">
            <Divider />
          </div>
        </div>
      );
    });
  };

  return (
    <div className="flex p-6 flex-col gap-6 md:px-40">
      <span className="text-2xl font-semibold text-neutral-800 md:text-4xl">
        Trips
      </span>
      <div
        className="flex items-start gap-6 self-stretch md:gap-12"
        role="tablist"
        aria-label="Trip lists"
      >
        {renderTab('Upcoming', upcomingTrips.length, 0)}
        {renderTab('Completed', completedTrips.length, 1)}
      </div>
      <div
        role="tabpanel"
        id={`trips-tabpanel-${activeTab}`}
        aria-labelledby={`trips-tab-${activeTab}`}
      >
        {activeTab === 0
          ? renderTrips(upcomingTrips, true)
          : renderTrips(completedTrips, false)}
      </div>
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        slotProps={{
          paper: {
            sx: {
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            },
          },
        }}
      >
        {selectedTripId && (
          <TripManagementDetail
            tripId={selectedTripId}
            booking={upcomingBookings.find(
              booking => booking.tripId === selectedTripId,
            )}
            onClose={handleCloseDrawer}
          />
        )}
      </Drawer>
      <TripItineraryModal
        open={isModalOpen}
        onClose={handleCloseModal}
        tripId={selectedTripId as number} // Use the selectedTripId for the modal
      />
    </div>
  );
};

export default TripManagement;
